/**
 * Invoice Summary Report — summaries, Recharts bar + pie, client grid, invoice list.
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  Paper,
  Skeleton,
  Alert,
  Chip,
  Link,
  Avatar,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DataGrid } from '@mui/x-data-grid';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import dayjs from 'dayjs';
import { enhancedApi } from '../../api';

const cellVal = (p) => {
  if (p == null) return p;
  if (typeof p === 'object' && 'value' in p) return p.value;
  return p;
};

const fmtMoney = (n) =>
  n != null && !Number.isNaN(n)
    ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const initials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length >= 2) return `${p[0][0]}${p[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const sliceColor = (status, theme) => {
  const k = String(status || '').toLowerCase();
  if (k === 'paid') return theme.palette.success.main;
  if (k === 'sent') return theme.palette.info.main;
  if (k === 'draft') return theme.palette.action.disabledBackground;
  if (k === 'overdue') return theme.palette.error.main;
  if (k === 'void' || k === 'voided') return theme.palette.grey[500];
  return theme.palette.grey[400];
};

const invoiceStatusChip = (status) => {
  const k = String(status || '').toLowerCase();
  if (k === 'paid') return { label: 'Paid', color: 'success' };
  if (k === 'sent') return { label: 'Sent', color: 'info' };
  if (k === 'draft') return { label: 'Draft', color: 'default', variant: 'outlined' };
  if (k === 'overdue') return { label: 'Overdue', color: 'error' };
  if (k === 'void' || k === 'voided') return { label: 'Void', color: 'default', variant: 'outlined' };
  return { label: status || '—', color: 'default', variant: 'outlined' };
};

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Safe list: null, {}, and non-arrays become []. Supports { results: [] } / { data: [] }. */
function coerceArray(v) {
  if (v == null) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'object') {
    if (Array.isArray(v.results)) return v.results;
    if (Array.isArray(v.data)) return v.data;
  }
  return [];
}

/** Month series may be an array or a map like { "2024-01": { invoiced, paid } }. */
function coerceMonthSeries(raw) {
  const fromList = coerceArray(raw);
  if (fromList.length > 0) return fromList;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return Object.entries(raw).map(([k, val]) =>
      val != null && typeof val === 'object' && !Array.isArray(val)
        ? { month: val.month ?? k, period: val.period ?? k, ...val }
        : { month: k, invoiced: Number(val) || 0, paid: 0 }
    );
  }
  return [];
}

function normalizeReportPayload(data) {
  const s = data?.summary ?? {};
  let collectionRate = null;
  if (s.collection_rate != null) {
    const n = Number(s.collection_rate);
    if (!Number.isNaN(n)) {
      collectionRate = n <= 1 && n >= 0 ? n * 100 : n;
    }
  }
  const summary = {
    total_invoiced: Number(s.total_invoiced ?? s.invoiced ?? 0),
    total_paid: Number(s.total_paid ?? s.paid ?? 0),
    outstanding: Number(s.outstanding ?? s.open ?? 0),
    overdue: Number(s.overdue ?? 0),
    collection_rate: collectionRate,
  };
  const revenue_by_month = coerceMonthSeries(data?.revenue_by_month ?? data?.monthly).map((row, i) => {
    const m = row.month ?? row.period ?? row.key ?? `M${i}`;
    const invoiced = Number(row.invoiced ?? row.total_invoiced ?? 0);
    const paid = Number(row.paid ?? row.total_paid ?? 0);
    const label =
      typeof m === 'string' && m.length >= 7
        ? dayjs(`${m}-01`).isValid()
          ? dayjs(`${m}-01`).format('MMM YY')
          : m
        : dayjs(m).isValid()
          ? dayjs(m).format('MMM YY')
          : String(m);
    return { monthKey: m, monthLabel: label, invoiced, paid };
  });
  const status_breakdown = coerceArray(data?.status_breakdown ?? data?.by_status).map((row) => ({
    status: String(row.status ?? row.key ?? '').toLowerCase() || 'other',
    count: Number(row.count ?? row.invoice_count ?? 0),
    amount: Number(row.amount ?? 0),
  }));
  const by_client = coerceArray(data?.by_client ?? data?.clients).map((r, i) => ({
    id: r.client_id ?? r.id ?? i,
    client_id: r.client_id ?? r.id,
    client_name: r.client_name ?? r.name ?? '—',
    total_invoiced: Number(r.total_invoiced ?? r.invoiced ?? 0),
    paid: Number(r.paid ?? r.total_paid ?? 0),
    outstanding: Number(r.outstanding ?? r.open ?? 0),
    overdue: Number(r.overdue ?? 0),
    invoice_count: Number(r.invoice_count ?? r.count ?? 0),
    avg_days_to_pay: r.avg_days_to_pay != null ? Number(r.avg_days_to_pay) : null,
  }));
  const invoices = coerceArray(data?.invoices).map((r, i) => ({
    id: r.id ?? i,
    invoice_number: r.invoice_number ?? r.number ?? String(r.id ?? ''),
    client_id: r.client_id,
    client_name: r.client_name ?? r.client ?? '—',
    amount: Number(r.amount ?? r.total ?? 0),
    status: String(r.status ?? 'draft').toLowerCase(),
    issue_date: r.issue_date ?? r.created_at ?? null,
    due_date: r.due_date ?? null,
    days_outstanding: r.days_outstanding != null ? Number(r.days_outstanding) : null,
  }));
  return { summary, revenue_by_month, status_breakdown, by_client, invoices };
}

function InvoiceSummaryReport() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState(() => dayjs().startOf('month'));
  const [dateTo, setDateTo] = useState(() => dayjs());
  const [groupBy, setGroupBy] = useState('month');
  const [statusFilter, setStatusFilter] = useState('all');
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [hasRun, setHasRun] = useState(false);
  const [showAllInvoices, setShowAllInvoices] = useState(false);

  const reportParams = useMemo(
    () => ({
      date_from: dateFrom.format('YYYY-MM-DD'),
      date_to: dateTo.format('YYYY-MM-DD'),
      group_by: groupBy,
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    }),
    [dateFrom, dateTo, groupBy, statusFilter]
  );

  const normalized = useMemo(() => normalizeReportPayload(raw ?? {}), [raw]);

  const runReport = useCallback(() => {
    setLoading(true);
    setError(null);
    setHasRun(true);
    enhancedApi
      .getInvoiceSummaryReport(reportParams)
      .then((res) => setRaw(res.data))
      .catch(() => {
        setError('Could not load invoice summary. The report API may not be available yet.');
        setRaw(null);
      })
      .finally(() => setLoading(false));
  }, [reportParams]);

  const handleExportCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const res = await enhancedApi.exportInvoiceSummaryCSV(reportParams);
      triggerBlobDownload(res.data, `invoice-summary-${reportParams.date_from}-${reportParams.date_to}.csv`);
    } catch {
      setError('Export failed. Ensure the export endpoint is configured.');
    } finally {
      setExporting(false);
    }
  };

  const pieData = useMemo(
    () =>
      normalized.status_breakdown
        .filter((d) => d.count > 0)
        .map((d) => ({
          name: d.status.charAt(0).toUpperCase() + d.status.slice(1),
          value: d.count,
          status: d.status,
        })),
    [normalized.status_breakdown]
  );
  const pieTotal = useMemo(() => pieData.reduce((a, b) => a + b.value, 0), [pieData]);

  const collectionPct = normalized.summary.collection_rate;
  const collectionChipColor =
    collectionPct == null ? 'default' : collectionPct >= 90 ? 'success' : collectionPct >= 70 ? 'warning' : 'error';

  const invoicesByStatus = useMemo(() => {
    const map = {};
    normalized.invoices.forEach((inv) => {
      const k = inv.status || 'other';
      if (!map[k]) map[k] = [];
      map[k].push(inv);
    });
    return map;
  }, [normalized.invoices]);

  const statusOrder = ['paid', 'sent', 'draft', 'overdue', 'void', 'voided'];

  const clientColumns = useMemo(
    () => [
      {
        field: 'client_name',
        headerName: 'Client',
        flex: 1,
        minWidth: 180,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.light' }}>
              {initials(params.row.client_name)}
            </Avatar>
            <Link
              component={RouterLink}
              to={`/client/${params.row.client_id}`}
              underline="hover"
              color="primary"
              fontWeight={600}
            >
              {params.row.client_name}
            </Link>
          </Box>
        ),
      },
      {
        field: 'total_invoiced',
        headerName: 'Total Invoiced',
        width: 130,
        valueFormatter: (p) => fmtMoney(cellVal(p)),
      },
      {
        field: 'paid',
        headerName: 'Paid',
        width: 110,
        valueFormatter: (p) => fmtMoney(cellVal(p)),
      },
      {
        field: 'outstanding',
        headerName: 'Outstanding',
        width: 120,
        valueFormatter: (p) => fmtMoney(cellVal(p)),
      },
      {
        field: 'overdue',
        headerName: 'Overdue',
        width: 100,
        renderCell: (p) => {
          const v = Number(cellVal(p)) || 0;
          return (
            <Typography variant="body2" fontWeight={v > 0 ? 700 : 400} color={v > 0 ? 'error.main' : 'text.primary'}>
              {fmtMoney(v)}
            </Typography>
          );
        },
      },
      { field: 'invoice_count', headerName: 'Invoices', width: 100, type: 'number' },
      {
        field: 'avg_days_to_pay',
        headerName: 'Avg Days to Pay',
        width: 140,
        renderCell: (p) => {
          const v = cellVal(p);
          if (v == null || Number.isNaN(v)) return '—';
          const n = Number(v);
          const color = n < 30 ? 'success.main' : n <= 60 ? 'warning.main' : 'error.main';
          return (
            <Typography variant="body2" fontWeight={600} sx={{ color }}>
              {n.toFixed(0)}
            </Typography>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 220,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={() => navigate(`/billing/invoices?clientId=${params.row.client_id ?? ''}`)}
            >
              View Invoices
            </Button>
            {params.row.outstanding > 0 ? (
              <Button
                size="small"
                variant="text"
                color="warning"
                onClick={() => navigate(`/billing/invoices?clientId=${params.row.client_id ?? ''}&remind=1`)}
              >
                Send Reminder
              </Button>
            ) : null}
          </Box>
        ),
      },
    ],
    [navigate]
  );

  const empty =
    hasRun &&
    !loading &&
    !error &&
    normalized.by_client.length === 0 &&
    normalized.revenue_by_month.length === 0 &&
    pieData.length === 0 &&
    normalized.invoices.length === 0 &&
    (normalized.summary.total_invoiced ?? 0) === 0 &&
    (normalized.summary.total_paid ?? 0) === 0;
  const hasContent =
    hasRun &&
    !loading &&
    !empty;

  const barChartData = normalized.revenue_by_month;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Invoice Summary Report
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Invoicing performance, aging, and collection metrics.
            </Typography>
          </Box>
          <Button variant="outlined" disabled={exporting || !hasRun} onClick={handleExportCsv}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <DatePicker label="From" value={dateFrom} onChange={(v) => v && setDateFrom(v)} slotProps={{ textField: { size: 'small' } }} />
            <DatePicker label="To" value={dateTo} onChange={(v) => v && setDateTo(v)} slotProps={{ textField: { size: 'small' } }} />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="inv-grp-label">Group By</InputLabel>
              <Select
                labelId="inv-grp-label"
                label="Group By"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
              >
                <MenuItem value="month">By Month</MenuItem>
                <MenuItem value="client">By Client</MenuItem>
                <MenuItem value="status">By Status</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="inv-st-label">Status</InputLabel>
              <Select
                labelId="inv-st-label"
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="void">Void</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" color="primary" onClick={runReport} disabled={loading}>
              Run Report
            </Button>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box>
            <Skeleton variant="rectangular" height={88} sx={{ borderRadius: 1, mb: 2 }} />
            <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1, mb: 2 }} />
            <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 1 }} />
          </Box>
        ) : empty ? (
          <Paper
            variant="outlined"
            sx={{ p: 6, textAlign: 'center', borderColor: 'divider', borderStyle: 'dashed', bgcolor: 'background.paper' }}
          >
            <ReceiptLongIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary" fontWeight={600}>
              No invoices for selected period
            </Typography>
          </Paper>
        ) : hasContent ? (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
                gap: 2,
                mb: 3,
              }}
            >
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', borderTop: 4, borderTopColor: 'primary.main' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total Invoiced
                </Typography>
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  {fmtMoney(normalized.summary.total_invoiced)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', borderTop: 4, borderTopColor: 'success.main' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total Paid
                </Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  {fmtMoney(normalized.summary.total_paid)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', borderTop: 4, borderTopColor: 'warning.main' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Outstanding
                </Typography>
                <Typography variant="h6" fontWeight={700} color="warning.main">
                  {fmtMoney(normalized.summary.outstanding)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', borderTop: 4, borderTopColor: 'error.main' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Overdue
                </Typography>
                <Typography variant="h6" fontWeight={700} color="error.main">
                  {fmtMoney(normalized.summary.overdue)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', borderTop: 4, borderTopColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Collection Rate
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    size="small"
                    label={collectionPct != null ? `${collectionPct.toFixed(1)}%` : '—'}
                    color={collectionChipColor}
                  />
                </Box>
              </Paper>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
                mb: 3,
              }}
            >
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', minHeight: 320 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Revenue by Month
                </Typography>
                <Box sx={{ width: '100%', height: 280 }}>
                  {barChartData.length > 0 ? (
                    <ResponsiveContainer>
                      <BarChart data={barChartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                        <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} />
                        <Tooltip
                          formatter={(value, name) => [fmtMoney(value), name === 'invoiced' ? 'Invoiced' : 'Paid']}
                          labelFormatter={(l) => l}
                        />
                        <Bar dataKey="invoiced" name="invoiced" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} maxBarSize={28} />
                        <Bar dataKey="paid" name="paid" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} maxBarSize={28} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No monthly series returned for this period.
                    </Typography>
                  )}
                </Box>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', minHeight: 320 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Invoice Status Breakdown
                </Typography>
                <Box sx={{ width: '100%', height: 260, position: 'relative' }}>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="45%"
                          innerRadius={56}
                          outerRadius={88}
                          paddingAngle={2}
                        >
                          {pieData.map((entry) => (
                            <Cell key={entry.status} fill={sliceColor(entry.status, theme)} stroke="transparent" />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => [`${v} invoices`, 'Count']} />
                        <Legend verticalAlign="bottom" height={36} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No status breakdown for this period.
                    </Typography>
                  )}
                </Box>
                <Typography align="center" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Total invoices: <strong>{pieTotal}</strong>
                </Typography>
              </Paper>
            </Box>

            {normalized.by_client.length > 0 && (
              <Paper variant="outlined" sx={{ borderColor: 'divider', overflow: 'hidden', mb: 2 }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    By Client
                  </Typography>
                </Box>
                <DataGrid
                  rows={normalized.by_client}
                  columns={clientColumns}
                  pageSizeOptions={[10, 25]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  disableRowSelectionOnClick
                  autoHeight
                  sx={{ border: 'none', '& .MuiDataGrid-columnHeaders': { bgcolor: 'action.hover' } }}
                />
              </Paper>
            )}

            {normalized.invoices.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Invoice list
                  </Typography>
                  <Button size="small" variant="text" onClick={() => setShowAllInvoices((v) => !v)}>
                    {showAllInvoices ? 'Hide' : 'View All'}
                  </Button>
                </Box>
                <Collapse in={showAllInvoices}>
                  {statusOrder
                    .filter((st) => invoicesByStatus[st]?.length)
                    .concat(
                      Object.keys(invoicesByStatus).filter((k) => !statusOrder.includes(k) && invoicesByStatus[k]?.length)
                    )
                    .map((st) => (
                      <Accordion key={st} defaultExpanded disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 1, '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography fontWeight={600}>
                            {st.charAt(0).toUpperCase() + st.slice(1)} ({invoicesByStatus[st].length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                          <DataGrid
                            rows={invoicesByStatus[st]}
                            getRowId={(r) => r.id}
                            columns={[
                              { field: 'invoice_number', headerName: 'Invoice #', width: 120 },
                              { field: 'client_name', headerName: 'Client', flex: 1, minWidth: 140 },
                              {
                                field: 'amount',
                                headerName: 'Amount',
                                width: 120,
                                valueFormatter: (p) => fmtMoney(cellVal(p)),
                              },
                              {
                                field: 'status',
                                headerName: 'Status',
                                width: 120,
                                renderCell: (p) => {
                                  const cfg = invoiceStatusChip(cellVal(p));
                                  return <Chip size="small" label={cfg.label} color={cfg.color} variant={cfg.variant || 'filled'} />;
                                },
                              },
                              {
                                field: 'issue_date',
                                headerName: 'Issue Date',
                                width: 120,
                                valueFormatter: (p) => {
                                  const v = cellVal(p);
                                  return v ? dayjs(v).format('MMM D, YYYY') : '—';
                                },
                              },
                              {
                                field: 'due_date',
                                headerName: 'Due Date',
                                width: 120,
                                valueFormatter: (p) => {
                                  const v = cellVal(p);
                                  return v ? dayjs(v).format('MMM D, YYYY') : '—';
                                },
                              },
                              {
                                field: 'days_outstanding',
                                headerName: 'Days Outstanding',
                                width: 130,
                                type: 'number',
                              },
                            ]}
                            hideFooter
                            autoHeight
                            disableRowSelectionOnClick
                            sx={{ border: 'none' }}
                          />
                        </AccordionDetails>
                      </Accordion>
                    ))}
                </Collapse>
              </Paper>
            )}
          </>
        ) : !hasRun ? (
          <Typography variant="body2" color="text.secondary">
            Choose filters and click <strong>Run Report</strong>.
          </Typography>
        ) : null}
      </Box>
    </LocalizationProvider>
  );
}

export default InvoiceSummaryReport;
